import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  PhoneCall,
  Calendar,
  Compass,
  Heart,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Users,
  Quote,
  Activity,
  ArrowUpRight,
  Flame
} from "lucide-react";

export default function Home() {
  const marqueeKeywords = [
    "Emotional Freedom",
    "Conscious Connection",
    "Inner Leadership",
    "Grounded Spirituality",
    "Energy Awareness",
    "Life Design",
    "Emotional Integration",
    "Self-Connection",
    "Conscious Choices"
  ];

  const painExperiences = [
    { text: "“I know what I should do, but I cannot seem to do it.”", sub: "Inner Resistance" },
    { text: "“Small things trigger a big emotional reaction.”", sub: "Hyper-Reactivity" },
    { text: "“I keep repeating the same relationship patterns.”", sub: "Familiar Cycles" },
    { text: "“I feel responsible for everyone's emotions.”", sub: "Boundaries & Over-Responsibility" },
    { text: "“I have achieved things, but I still don't feel peaceful.”", sub: "Empty Achievement" },
    { text: "“I struggle to say no without guilt.”", sub: "People-Pleasing Pattern" },
    { text: "“I feel emotionally exhausted.”", sub: "Overwhelm & Burnout" },
    { text: "“I want to change, but I don't know where to begin.”", sub: "Awaiting a Path" }
  ];

  const currentVsFuture = [
    { from: "Emotional overwhelm", to: "Emotional awareness" },
    { from: "Self-doubt", to: "Self-worth" },
    { from: "People-pleasing", to: "Healthy boundaries" },
    { from: "Fear-based decisions", to: "Conscious decisions" },
    { from: "Suppressed emotions", to: "Emotional expression" },
    { from: "Relationship tension", to: "Conscious relationships" },
    { from: "Feeling stuck", to: "Clarity" },
    { from: "Low clarity", to: "Inner stability" },
    { from: "Reactive patterns", to: "Intentional responses" },
    { from: "Disconnection from self", to: "Self-connection" }
  ];

  const eshaMethodStages = [
    { num: "01", name: "Awareness", desc: "See what is happening." },
    { num: "02", name: "Emotional Freedom", desc: "Understand and work with emotional patterns." },
    { num: "03", name: "Self Mastery", desc: "Strengthen self-worth, identity and inner leadership." },
    { num: "04", name: "Relationship Mastery", desc: "Create healthier communication, boundaries and connection." },
    { num: "05", name: "Abundance", desc: "Develop conscious beliefs and behaviours around wealth and possibility." },
    { num: "06", name: "Life Mastery", desc: "Design life around purpose, leadership and impact." },
    { num: "07", name: "Contribution", desc: "Use personal growth to create meaningful service." }
  ];

  return (
    <main className="min-h-screen relative bg-[#050505] text-[#F6F4EE]" data-testid="homepage">
      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[95vh] flex items-center justify-center pt-28 pb-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/eswari-photo.jpg"
            alt="Eswari Podugu with a radiant golden lotus — ESHA"
            className="w-full h-full object-cover opacity-15 object-[42%_27%] lg:object-[50%_46%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="flex justify-center mb-6"
          >
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] px-4 py-2 border border-[#00D2FF]/20 bg-[#111111]/90 rounded-full backdrop-blur-md font-bold">
              Eternal Soul Healing & Awakening
            </span>
          </motion.div>

          <div className="overflow-hidden mb-6">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-[#F6F4EE] leading-[1.1] max-w-5xl mx-auto"
            >
              Your life can change when you change the way you <span className="text-gold-shimmer italic">understand yourself.</span>
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="font-sans text-base sm:text-lg lg:text-xl text-[#AAA29E] max-w-3xl mx-auto mb-12 leading-relaxed"
          >
            Understand your emotions. Reconnect with yourself. Transform your relationships. Create a more conscious, abundant and meaningful life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6"
          >
            <a
              href="https://learn.eshawitheswari.com/l/96a7c7eae0"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full shadow-[0_8px_30px_rgba(255,140,0,0.3)] hover:scale-105 active:scale-95 transition-all text-center"
              data-testid="hero-primary-cta-button"
            >
              Attend the Emotional Freedom Masterclass
            </a>
            <a
              href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 border border-[#FF8C00]/25 hover:border-[#FF8C00]/50 text-[#F6F4EE] font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all text-center bg-[#111111]/70 backdrop-blur-md"
              data-testid="hero-secondary-cta-button"
            >
              Book a Clarity Call
            </a>
          </motion.div>
        </div>
      </section>

      {/* EDITORIAL SLOW MARQUEE */}
      <section className="py-6 border-y border-[#FF8C00]/15 bg-[#111111] overflow-hidden" aria-hidden="true">
        <div className="relative w-full flex items-center">
          <div className="animate-marquee whitespace-nowrap flex gap-12 text-xs font-mono tracking-[0.25em] uppercase text-[#FF8C00] font-bold">
            {marqueeKeywords.concat(marqueeKeywords).map((keyword, index) => (
              <span key={index} className="flex items-center gap-3">
                <span>{keyword}</span>
                <span className="text-[#00D2FF]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — PAIN / RECOGNITION */}
      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="pain-recognition-section">
        <div className="absolute inset-0 glow-radial pointer-events-none opacity-30" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-3 font-bold">
              The Human Experience
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight">
              Does any of this sound familiar?
            </h2>
            <p className="font-sans text-sm text-[#AAA29E] mt-4">
              We carry patterns built in moments we survived, which now limit the way we live.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painExperiences.map((exp, idx) => (
              <div
                key={idx}
                className="bg-[#0F0F0F]/60 border border-[#FF8C00]/15 p-8 rounded-xl flex flex-col justify-between hover:border-[#00D2FF]/45 transition-all duration-300 group shadow-md"
                data-testid={`pain-card-${idx}`}
              >
                <div>
                  <div className="text-xs font-mono text-[#00D2FF] uppercase tracking-widest mb-6 font-bold">
                    {exp.sub}
                  </div>
                  <p className="font-serif italic text-lg sm:text-xl text-[#F6F4EE] leading-relaxed">
                    {exp.text}
                  </p>
                </div>
                <div className="mt-8 flex items-center justify-end">
                  <div className="h-6 w-6 rounded-full border border-[#FF8C00]/25 flex items-center justify-center text-xs font-mono text-[#00D2FF] group-hover:bg-[#00D2FF]/10 group-hover:border-[#00D2FF]/50 transition-colors">
                    +
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — "MAYBE YOU ARE NOT BROKEN" */}
      <section className="py-24 sm:py-32 bg-[#111111]/40 border-y border-[#FF8C00]/15 relative" data-testid="not-broken-section">
        <div className="absolute inset-0 glow-copper pointer-events-none opacity-15" />
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] block mb-4 font-bold">
            A Compassionate Truth
          </span>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-light text-[#F6F4EE] leading-[1.15] max-w-4xl mx-auto mb-8">
            Maybe you are not broken. Maybe you have been carrying too much for too long.
          </h2>
          <p className="font-sans text-base sm:text-lg text-[#AAA29E] max-w-2xl mx-auto mb-10 leading-relaxed">
            You don&apos;t need more self-judgment. You need awareness, understanding and a compassionate path forward.
          </p>
          <Link
            to="/offerings"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all duration-300 shadow-md"
            data-testid="begin-with-awareness-cta"
          >
            Begin With Awareness
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* SECTION 4 — THE HIDDEN PATTERNS */}
      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="hidden-patterns-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-3 font-bold">
                Under the Surface
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight mb-6">
                What if the problem is not your life — but the patterns running underneath it?
              </h2>
              <p className="font-sans text-base text-[#AAA29E] mb-8 leading-relaxed">
                Most of our struggles are not random. They are the logical outcome of automated responses that function below our waking consciousness. When we live from automatic patterns, we recreate the same life repeatedly.
              </p>

              <div className="space-y-4">
                {[
                  { title: "Thoughts", desc: "Influence your active emotional landscape." },
                  { title: "Emotions", desc: "Guide and drive your immediate reactions." },
                  { title: "Repeated Reactions", desc: "Solidify over time to become deep subconscious patterns." },
                  { title: "Patterns", desc: "Secretly shape every major relationship and critical decision." },
                  { title: "Repeated Decisions", desc: "Ultimately construct the outer reality of your life." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-start bg-[#0F0F0F] p-4 rounded-lg border border-[#FF8C00]/15 shadow-md">
                    <span className="font-mono text-xs font-bold text-[#00D2FF] mt-0.5 bg-[#00D2FF]/10 px-2 py-0.5 rounded">
                      0{idx + 1}
                    </span>
                    <div>
                      <h4 className="font-serif text-[#F6F4EE] text-base font-bold">{item.title}</h4>
                      <p className="font-sans text-xs text-[#AAA29E] mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 glow-radial opacity-20" />
              <div className="bg-[#0F0F0F]/80 border border-[#FF8C00]/20 p-8 sm:p-12 rounded-2xl relative z-10 shadow-lg">
                <h3 className="font-serif text-xl sm:text-2xl text-[#F6F4EE] text-center mb-8 font-normal tracking-wide">
                  The Pathway to Sovereign Choice
                </h3>

                <div className="flex flex-col gap-6 relative">
                  <div className="absolute left-[20px] top-4 bottom-4 w-[1px] bg-gradient-to-b from-[#00D2FF] via-[#C87D53] to-transparent" />
                  {[
                    { step: "Awareness", icon: Compass, color: "text-[#00D2FF]", bg: "bg-[#00D2FF]/10", desc: "Becoming conscious of what is actually running." },
                    { step: "Choice", icon: Sparkles, color: "text-[#C87D53]", bg: "bg-[#C87D53]/10", desc: "Discovering that a different perspective is available." },
                    { step: "Response", icon: Activity, color: "text-emerald-400", bg: "bg-emerald-400/15", desc: "Pausing the automatic trigger to act intentionally." },
                    { step: "Pattern Shift", icon: Zap, color: "text-[#FF8C00]", bg: "bg-[#FF8C00]/10", desc: "Rewriting the underlying program through repetition." },
                    { step: "Conscious Life", icon: Heart, color: "text-red-400", bg: "bg-red-400/10", desc: "Living with inner peace, agency, and clarity." }
                  ].map((p, i) => (
                    <div key={i} className="flex gap-4 items-start relative z-10">
                      <div className={`h-10 w-10 rounded-full ${p.bg} flex items-center justify-center border border-[#FF8C00]/25`}>
                        <p.icon className={`h-4 w-4 ${p.color}`} />
                      </div>
                      <div>
                        <h4 className="font-serif text-[#F6F4EE] text-base font-bold">{p.step}</h4>
                        <p className="font-sans text-xs text-[#AAA29E] mt-0.5">{p.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 & 6 — EMOTIONAL INTELLIGENCE & ENERGY AWARENESS */}
      <section className="py-24 sm:py-32 bg-[#111111]/20 border-t border-[#FF8C00]/15 relative" data-testid="eq-energy-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Emotional Intelligence */}
            <div className="bg-[#0F0F0F]/80 border border-[#FF8C00]/15 p-10 sm:p-12 rounded-2xl relative shadow-sm">
              <div className="absolute top-0 right-0 h-24 w-24 glow-radial opacity-20 pointer-events-none" />
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-4 font-bold">
                EQ Development
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-[#F6F4EE] leading-tight mb-6">
                Your emotions are information — not your identity.
              </h3>
              <p className="font-sans text-sm text-[#AAA29E] mb-8 leading-relaxed">
                Emotions represent raw signals from our nervous system and lived history. They tell us what we care about, where we feel unprotected, and where we seek connection. When you learn to listen without collapsing into them, they become your greatest source of guidance.
              </p>
              <ul className="space-y-3.5 mb-8">
                {[
                  "Recognize and unpack triggers before reacting",
                  "Understand internal neurological signals",
                  "Gain the capacity to pause before automatic behaviors",
                  "Express deep emotions consciously and safely",
                  "Cultivate a sustainable bedrock of self-awareness"
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-2.5 items-start text-sm text-[#F6F4EE]/90 font-medium">
                    <CheckCircle2 className="h-4 w-4 text-[#00D2FF] shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/offerings"
                className="text-xs uppercase tracking-widest font-mono text-[#00D2FF] hover:text-[#C87D53] transition-colors flex items-center gap-1 font-bold"
                data-testid="eq-cta-button"
              >
                Discover Emotional Freedom ↗
              </Link>
            </div>

            {/* Energy Awareness */}
            <div className="bg-[#0F0F0F]/80 border border-[#FF8C00]/15 p-10 sm:p-12 rounded-2xl relative shadow-sm">
              <div className="absolute top-0 right-0 h-24 w-24 glow-copper opacity-20 pointer-events-none" />
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-4 font-bold">
                Sovereign Presence
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-[#F6F4EE] leading-tight mb-6">
                Your inner state changes the way you experience your outer world.
              </h3>
              <p className="font-sans text-sm text-[#AAA29E] mb-8 leading-relaxed">
                Energy is not mystical; it is physical, physiological, and emotional. It is your active level of presence, nervous-system arousal, attention, and intent. ESHA integrates a highly grounded, practical approach to managing personal energy.
              </p>
              <ul className="space-y-3.5 mb-8">
                {[
                  "Identify where your vital attention is leaking daily",
                  "Nervous-system regulation and physiological calm",
                  "Sustain healthy presence in demanding environments",
                  "Harness restorative habits over chronic depletion",
                  "Communicate from grounded authenticity rather than fear"
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-2.5 items-start text-sm text-[#F6F4EE]/90 font-medium">
                    <CheckCircle2 className="h-4 w-4 text-[#C87D53] shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/programs"
                className="text-xs uppercase tracking-widest font-mono text-[#C87D53] hover:text-[#00D2FF] transition-colors flex items-center gap-1 font-bold"
                data-testid="energy-cta-button"
              >
                Explore Energy Alignment ↗
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — CURRENT SELF → FUTURE SELF */}
      <section className="py-24 sm:py-32 relative bg-[#050505] overflow-hidden" data-testid="transformation-matrix-section">
        <div className="absolute inset-0 glow-radial pointer-events-none opacity-15" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
              The Path of Evolution
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE]">
              From surviving your life to consciously creating it.
            </h2>
          </div>

          <div className="border border-[#FF8C00]/15 rounded-2xl bg-[#0F0F0F]/30 overflow-hidden shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#FF8C00]/15">
              {/* FROM column */}
              <div className="p-8 sm:p-12 bg-red-500/5">
                <h3 className="font-serif text-xl sm:text-2xl text-red-400 font-light tracking-wider mb-8 uppercase flex items-center gap-2 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  The Pattern of Survival (FROM)
                </h3>
                <div className="space-y-4">
                  {currentVsFuture.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2.5 border-b border-[#FF8C00]/5 text-sm text-[#AAA29E]">
                      <span>{item.from}</span>
                      <span className="text-red-400 text-xs">✕</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* TO column */}
              <div className="p-8 sm:p-12 bg-emerald-500/5">
                <h3 className="font-serif text-xl sm:text-2xl text-emerald-400 font-light tracking-wider mb-8 uppercase flex items-center gap-2 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  The Choice of Awareness (TO)
                </h3>
                <div className="space-y-4">
                  {currentVsFuture.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2.5 border-b border-[#FF8C00]/5 text-sm text-[#F6F4EE] font-bold">
                      <span>{item.to}</span>
                      <span className="text-emerald-400 text-xs">✓</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — ESHA METHOD */}
      <section className="py-24 sm:py-32 bg-[#111111]/50 border-y border-[#FF8C00]/15 relative" data-testid="esha-method-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
              The Framework
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight">
              A journey from awareness to mastery.
            </h2>
            <p className="font-sans text-sm text-[#AAA29E] mt-4 max-w-2xl mx-auto">
              ESHA integrates psychological awareness, energy tools, and practical life-design into seven structured, highly-actionable developmental stages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {eshaMethodStages.map((stage, idx) => (
              <div
                key={idx}
                className="bg-[#050505] border border-[#FF8C00]/15 p-8 rounded-xl relative overflow-hidden group hover:border-[#00D2FF]/45 transition-all duration-300 shadow-sm"
                data-testid={`method-card-${idx}`}
              >
                <div className="font-mono text-3xl font-bold text-[#00D2FF]/30 mb-6 group-hover:text-[#00D2FF]/50 transition-colors">
                  {stage.num}
                </div>
                <h3 className="font-serif text-lg sm:text-xl text-[#F6F4EE] font-bold mb-3">
                  {stage.name}
                </h3>
                <p className="font-sans text-xs text-[#AAA29E] leading-relaxed">
                  {stage.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — ESHA ECOSYSTEM */}
      <section className="py-24 sm:py-32 bg-[#050505] relative" data-testid="programs-ecosystem-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-3 font-bold">
              Transformational Ecosystem
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE]">
              The Pathways of Transformation
            </h2>
            <p className="font-sans text-sm text-[#AAA29E] mt-4">
              Explore ESHA&apos;s premium pathways, designed to take you from foundational awareness to total life alignment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-[#0F0F0F]/60 border border-[#FF8C00]/15 p-8 rounded-2xl flex flex-col justify-between hover:border-[#00D2FF]/45 transition-all shadow-sm">
              <div>
                <span className="font-mono text-[10px] tracking-widest text-[#00D2FF] uppercase bg-[#00D2FF]/10 px-3 py-1 rounded-full font-bold">
                  Foundation
                </span>
                <h3 className="font-serif text-2xl text-[#F6F4EE] font-light mt-6 mb-4">
                  Emotional Freedom Blueprint
                </h3>
                <p className="font-sans text-sm text-[#AAA29E] mb-6 leading-relaxed">
                  A comprehensive foundation for understanding emotions, triggers, subconscious patterns, and establishing somatic nervous-system regulation.
                </p>
              </div>
              <Link
                to="/programs"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#00D2FF] font-bold hover:translate-x-1 transition-transform"
                data-testid="explore-blueprint-btn"
              >
                Explore Pathway <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="bg-[#0F0F0F]/60 border border-[#FF8C00]/15 p-8 rounded-2xl flex flex-col justify-between hover:border-[#00D2FF]/45 transition-all shadow-sm">
              <div>
                <span className="font-mono text-[10px] tracking-widest text-[#C87D53] uppercase bg-[#C87D53]/10 px-3 py-1 rounded-full font-bold">
                  Mastery Pillars
                </span>
                <h3 className="font-serif text-2xl text-[#F6F4EE] font-light mt-6 mb-4">
                  The Mastery Chambers
                </h3>
                <p className="font-sans text-sm text-[#AAA29E] mb-6 leading-relaxed">
                  Four specialized mastery tracks designed for deep growth: Self Mastery, Relationship Mastery, Emotional Mastery, and Abundance Mastery.
                </p>
              </div>
              <Link
                to="/programs"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#C87D53] font-bold hover:translate-x-1 transition-transform"
                data-testid="explore-mastery-btn"
              >
                Explore Pathways <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="bg-[#0F0F0F]/60 border border-[#FF8C00]/15 p-8 rounded-2xl flex flex-col justify-between hover:border-[#00D2FF]/45 transition-all shadow-sm">
              <div>
                <span className="font-mono text-[10px] tracking-widest text-[#FF8C00] uppercase bg-[#FF8C00]/10 px-3 py-1 rounded-full font-bold">
                  Integration
                </span>
                <h3 className="font-serif text-2xl text-[#F6F4EE] font-light mt-6 mb-4">
                  ESHA Life Mastery
                </h3>
                <p className="font-sans text-sm text-[#AAA29E] mb-6 leading-relaxed">
                  An integrated ultimate journey focused on consciously designing life around purpose, leadership, impact, and deep energetic presence.
                </p>
              </div>
              <Link
                to="/programs"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#FF8C00] font-bold hover:translate-x-1 transition-transform"
                data-testid="explore-life-mastery-btn"
              >
                Explore Pathway <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 — EMOTIONAL FREEDOM MASTERCLASS */}
      <section className="py-24 sm:py-32 bg-[#111111]/40 border-y border-[#FF8C00]/15 relative overflow-hidden" data-testid="masterclass-section">
        <div className="absolute inset-0 glow-radial pointer-events-none opacity-30" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
                Gateway Experience
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight mb-6">
                Start with one powerful conversation with yourself.
              </h2>
              <p className="font-sans text-base text-[#AAA29E] mb-8 leading-relaxed">
                The Emotional Freedom Masterclass is ESHA&apos;s most accessible entry point. In this intensive digital experience, you will learn to map your recurring emotional triggers, recognize deep relationship patterns, and discover somatic techniques for settling a hyper-vigilant nervous system.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  "Understanding raw emotional freedom",
                  "Why triggers mirror your past history",
                  "Moving awareness into practical choice",
                  "What somatic practices settle anxiety",
                  "Actionable tools you can use immediately"
                ].map((pt, i) => (
                  <div key={i} className="flex gap-2.5 items-start text-sm text-[#F6F4EE]/90 font-medium">
                    <CheckCircle2 className="h-4.5 w-4.5 text-[#00D2FF] shrink-0 mt-0.5" />
                    <span>{pt}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                <a
                  href="https://learn.eshawitheswari.com/l/96a7c7eae0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full text-center shadow-md hover:scale-105 active:scale-95 transition-all"
                  data-testid="masterclass-promo-cta-button"
                >
                  Reserve My Masterclass Seat
                </a>
                <span className="text-xs text-[#AAA29E]/80 text-center sm:text-left font-mono font-bold">
                  *Check local listings on Offerings Hub
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative z-10 border border-[#FF8C00]/20 rounded-2xl bg-[#050505] p-8 shadow-sm overflow-hidden">
                <div className="absolute top-0 right-0 h-20 w-20 glow-radial opacity-20" />
                <h3 className="font-serif text-xl text-[#F6F4EE] mb-4 border-b border-[#FF8C00]/15 pb-3 font-bold">
                  Masterclass Highlight
                </h3>
                <div className="space-y-4 text-xs font-sans leading-relaxed text-[#AAA29E]">
                  <p>
                    <strong>Length:</strong> 2 Hours Interactive Experience
                  </p>
                  <p>
                    <strong>Format:</strong> Streamed Digital Session
                  </p>
                  <p>
                    <strong>For Whom:</strong> Anyone seeking a practical, grounded method to transform reactivity and emotional burnout.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-[#FF8C00]/15 flex justify-between items-center">
                  <span className="text-xs uppercase tracking-wider font-mono text-[#00D2FF] font-bold">
                    Access Now
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-[#00D2FF]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 11 — ABOUT ESWARI */}
      <section className="py-24 sm:py-32 relative bg-[#050505] overflow-hidden" data-testid="about-eswari-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 relative">
              <div className="absolute inset-0 glow-radial opacity-20" />
              <div className="relative border border-[#FF8C00]/15 rounded-2xl overflow-hidden shadow-md aspect-[4/5] bg-[#0F0F0F]">
                <img
                  src="/assets/eswari-founder.png"
                  alt="Eswari Podugu — Founder of ESHA, Eternal Soul Healing and Awakening"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 via-transparent to-transparent p-6 text-center">
                  <p className="font-serif text-lg text-[#F6F4EE] font-bold">Eswari Podugu</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#00D2FF] mt-1 font-bold">Founder of ESHA</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
                The Founder&apos;s Journey
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight mb-6">
                Meet Eswari Podugu
              </h2>
              <p className="font-sans text-base text-[#AAA29E] mb-6 leading-relaxed">
                Eswari&apos;s journey has been shaped by real-life challenges, self-discovery, learning, healing, and transformation. She explored emotional awareness, energy work, personal development, and conscious living not simply as abstract academic concepts, but as mandatory tools for rebuilding the way she experienced herself and her life.
              </p>
              <p className="font-sans text-base text-[#AAA29E] mb-8 leading-relaxed">
                Her purpose evolved naturally from personal transformation into global service: If awareness can change one life, it can become a structured system that helps many lives.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl shadow-sm">
                  <h4 className="font-serif text-[#00D2FF] text-base font-bold mb-1">Founder of ESHA</h4>
                  <p className="font-sans text-xs text-[#AAA29E]">Creator of the seven-stage ESHA transformation methodology.</p>
                </div>
                <div className="p-4 bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl shadow-sm">
                  <h4 className="font-serif text-[#C87D53] text-base font-bold mb-1">Life & Relationship Coach</h4>
                  <p className="font-sans text-xs text-[#AAA29E]">Specialized in emotional intelligence, nervous system calm, and energy awareness.</p>
                </div>
              </div>

              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#0F0F0F] border border-[#FF8C00]/25 text-[#F6F4EE] hover:bg-[#00D2FF] hover:text-[#050505] font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all duration-300 shadow-sm"
                data-testid="discover-eswari-journey-btn"
              >
                Discover Eswari&apos;s Journey
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 12 — TESTIMONIALS */}
      <section className="py-24 sm:py-32 bg-[#111111]/50 border-y border-[#FF8C00]/15 relative" data-testid="testimonials-section">
        <div className="absolute inset-0 glow-radial pointer-events-none opacity-20" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
              Sacred Witness
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE]">
              Real people. Real shifts. Real journeys.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                quote: "“ESHA taught me to look at my anger as an alarm system rather than a character flaw. It completely saved my relationship with my children.”",
                name: "Ananya R.",
                details: "Emotional Freedom Graduate"
              },
              {
                quote: "“I used to feel exhausted trying to keep everyone happy. Setting boundaries without guilt felt impossible. Now, I feel lighter, and my business is actually growing because of it.”",
                name: "Vikram S.",
                details: "Self Mastery Chamber Alumni"
              },
              {
                quote: "“Understanding my relationship patterns at ESHA helped me and my partner speak to each other with actual care rather than triggering defensive arguments. Highly recommended.”",
                name: "Priya & Amit",
                details: "Relationship Mastery Alumni"
              }
            ].map((t, i) => (
              <div
                key={i}
                className="bg-[#050505] border border-[#FF8C00]/15 p-8 sm:p-10 rounded-2xl flex flex-col justify-between hover:border-[#00D2FF]/45 transition-all shadow-sm relative"
                data-testid={`testimonial-card-${i}`}
              >
                <Quote className="h-8 w-8 text-[#00D2FF]/20 absolute top-6 left-6" />
                <div className="pt-6">
                  <p className="font-serif italic text-base sm:text-lg text-[#F6F4EE] leading-relaxed mb-8">
                    {t.quote}
                  </p>
                </div>
                <div className="border-t border-[#FF8C00]/10 pt-6">
                  <h4 className="font-serif text-sm font-bold text-[#F6F4EE]">{t.name}</h4>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#00D2FF] mt-1 font-bold">{t.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 13 — TRANSFORMATION STORIES */}
      <section className="py-24 sm:py-32 relative bg-[#050505] overflow-hidden" data-testid="transformation-stories-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-3 font-bold">
              Case Studies
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE]">
              Detailed Transformation Stories
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "From Constant Burnout to Deep Quiet Stability",
                before: "Felt chronically overwhelmed, responsible for everyone's moods at home and work. Constantly exhausted.",
                awareness: "Realized her worth was tied to her over-functioning and fixing everyone else's problems.",
                shift: "Started honoring her own boundaries, somatic settling, and saying 'no' to over-commitment.",
                after: "Enjoys organic inner stability, authentic communication, and has recovered high vitality."
              },
              {
                title: "Rewriting Defensive Relationship Friction",
                before: "Experienced repeated reactive conflicts with partner, each feeling defensive and emotionally isolated.",
                awareness: "Recognized that current triggers were mirrors of childhood protection mechanisms.",
                shift: "Learned to pause before responding, somatic soothing, and conscious vulnerable communication.",
                after: "Built a deeply conscious, loving relationship grounded in mutual respect and safety."
              }
            ].map((story, i) => (
              <div
                key={i}
                className="bg-[#0F0F0F]/60 border border-[#FF8C00]/15 p-8 sm:p-10 rounded-2xl shadow-sm hover:border-[#00D2FF]/45 transition-all"
                data-testid={`story-card-${i}`}
              >
                <h3 className="font-serif text-xl sm:text-2xl text-[#F6F4EE] mb-6 border-b border-[#FF8C00]/15 pb-3 font-bold">
                  {story.title}
                </h3>
                <div className="space-y-4">
                  {[
                    { label: "Before", text: story.before, color: "text-red-400/80" },
                    { label: "Awareness", text: story.awareness, color: "text-[#00D2FF]" },
                    { label: "Shift", text: story.shift, color: "text-[#C87D53]" },
                    { label: "After", text: story.after, color: "text-emerald-400" }
                  ].map((block, sIdx) => (
                    <div key={sIdx} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 sm:gap-4 items-start text-sm">
                      <span className={`sm:col-span-3 font-mono text-xs uppercase tracking-wider font-bold ${block.color}`}>
                        {block.label}:
                      </span>
                      <p className="sm:col-span-9 text-[#AAA29E] font-sans text-xs sm:text-sm">{block.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 14 & 15 — VISION & MISSION */}
      <section className="py-24 sm:py-32 bg-[#111111]/20 border-t border-[#FF8C00]/15 relative" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Vision */}
            <div>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
                The Horizon
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] mb-6">
                ESHA Vision
              </h2>
              <p className="font-sans text-sm text-[#AAA29E] leading-relaxed mb-8">
                A world where emotional awareness becomes a natural way of life. We envision communities grounded in mutual respect, healthy emotional boundaries, responsible personal abundance, and conscious stewardship of our beautiful natural environment.
              </p>
              <div className="space-y-4">
                {[
                  "Cultivating emotionally mature leaders and parents",
                  "Establishing family systems grounded in boundary-safety",
                  "Integrating inner wealth mindset with ethical action",
                  "Deep connection to nature and ecological stewardship"
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start font-medium">
                    <Compass className="h-4.5 w-4.5 text-[#00D2FF] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#F6F4EE]/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mission */}
            <div>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#C87D53] block mb-3 font-bold">
                The Charge
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] mb-6">
                ESHA Mission
              </h2>
              <p className="font-sans text-sm text-[#AAA29E] leading-relaxed mb-8">
                Teach, Transform, Support, and Serve. We are committed to making profound emotional wisdom highly actionable, and offering sanctuary settings where people do not feel alone.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: "Teachings", desc: "Making transformational knowledge practical." },
                  { title: "Support", desc: "Forming supportive circles where people feel seen." },
                  { title: "Service", desc: "Contributing to the outer dignity and wellness of community." },
                  { title: "Awareness", desc: "Promoting somatic emotional freedom everywhere." }
                ].map((m, idx) => (
                  <div key={idx} className="p-4 bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl shadow-sm">
                    <h4 className="font-serif text-sm font-bold text-[#F6F4EE] mb-1">{m.title}</h4>
                    <p className="font-sans text-xs text-[#AAA29E]">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 16 — COMMUNITY */}
      <section className="py-24 sm:py-32 relative bg-[#050505]" data-testid="community-section">
        <div className="absolute inset-0 glow-radial opacity-20" />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
            Sovereign Circle
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] mb-6">
            You don&apos;t have to grow alone.
          </h2>
          <p className="font-sans text-base text-[#AAA29E] max-w-2xl mx-auto mb-10 leading-relaxed">
            The ESHA community represents an international collection of brave individuals committed to profound self-reflection, continuous practice, mutual encouragement, and raw conscious conversations.
          </p>
          <Link
            to="/offerings"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0F0F0F] border border-[#FF8C00]/25 hover:border-[#00D2FF]/50 text-[#F6F4EE] font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all shadow-sm"
            data-testid="explore-community-cta-button"
          >
            Explore ESHA Community Hub
            <Users className="h-4 w-4 text-[#00D2FF]" />
          </Link>
        </div>
      </section>

      {/* SECTION 17 — UPCOMING Saturday Workshops */}
      <section className="py-24 sm:py-32 bg-[#111111]/50 border-y border-[#FF8C00]/15 relative overflow-hidden" data-testid="saturday-workshops-section">
        <div className="absolute inset-0 glow-copper opacity-15" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
                Weekly Ritual
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] mb-6">
                Upcoming Saturday Workshops
              </h2>
              <p className="font-sans text-base text-[#AAA29E] mb-8 leading-relaxed">
                Every Saturday, ESHA hosts specialized intensive workshop sessions designed to unpack single concepts—such as setting high boundaries, decoding money patterns, somatic settling, and deeper emotional inquiry. Access our newly integrated Offerings Hub to see the current Saturday calendar schedules.
              </p>
              <Link
                to="/offerings"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full shadow-md"
                data-testid="workshops-calendar-link"
              >
                Explore Saturday Workshops Hub
                <Calendar className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-[#FF8C00]/20 rounded-2xl bg-[#050505] p-8 shadow-sm relative">
                <span className="absolute top-4 right-4 h-2 w-2 rounded-full bg-[#00D2FF]" />
                <h4 className="font-serif text-lg text-[#F6F4EE] mb-4 border-b border-[#FF8C00]/15 pb-3 font-bold">
                  This Saturday Experience
                </h4>
                <div className="space-y-4">
                  <div className="flex gap-3 text-sm">
                    <span className="font-mono text-xs text-[#00D2FF] font-bold">Topic:</span>
                    <span className="text-[#AAA29E]">Weekly Specialized Somatic/EQ Inquiry</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="font-mono text-xs text-[#00D2FF] font-bold">Location:</span>
                    <span className="text-[#AAA29E]">Live Interactive Somatic Studio</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="font-mono text-xs text-[#00D2FF] font-bold">Details:</span>
                    <span className="text-[#AAA29E]">Please view the live ESHA Offerings Hub for precise current topics, seat registrations, and enrollment details.</span>
                  </div>
                </div>
                <div className="mt-8 pt-4 border-t border-[#FF8C00]/15 flex justify-end">
                  <Link
                    to="/offerings"
                    className="text-xs uppercase tracking-widest font-mono text-[#00D2FF] flex items-center gap-1 hover:translate-x-1 transition-transform font-bold"
                    data-testid="saturday-workshop-secondary-calendar-link"
                  >
                    View Offerings <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 18 — CLARITY CALL */}
      <section className="py-24 sm:py-32 relative bg-[#050505]" data-testid="clarity-call-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="bg-[#0F0F0F]/50 border border-[#FF8C00]/15 rounded-3xl p-8 sm:p-16 relative overflow-hidden shadow-sm">
            <div className="absolute inset-0 glow-radial opacity-20" />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10 items-center">
              <div>
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#00D2FF] block mb-3 font-bold">
                  Private Advisory
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight mb-6">
                  Not sure where to start?
                </h2>
                <p className="font-sans text-base text-[#AAA29E] mb-8 leading-relaxed">
                  You don&apos;t need to know the entire journey. You only need to know your next step. Book a dedicated Clarity Call to speak with our certified advisors, map your current block, and select the ideal ESHA pathway for you.
                </p>

                <div className="space-y-3.5 mb-8">
                  {[
                    "Unpack your primary current growth bottleneck",
                    "Determine whether ESHA is the appropriate fit for you",
                    "Formulate a custom pathway of program enrollment"
                  ].map((p, i) => (
                    <div key={i} className="flex gap-2.5 items-start text-sm text-[#F6F4EE]/90 font-medium">
                      <CheckCircle2 className="h-4.5 w-4.5 text-[#00D2FF] shrink-0" />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-6 bg-[#050505] p-8 rounded-2xl border border-[#FF8C00]/15 text-center shadow-sm">
                <PhoneCall className="h-8 w-8 text-[#00D2FF] mx-auto mb-2" />
                <h3 className="font-serif text-xl sm:text-2xl text-[#F6F4EE] font-light">
                  Apply for a Clarity Call
                </h3>
                <p className="font-sans text-xs text-[#AAA29E] leading-relaxed">
                  Calls are reserved strictly for individuals seeking deep transformational work and ready to commit to self-responsibility.
                </p>
                <a
                  href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 bg-[#0F0F0F] border border-[#FF8C00]/25 text-[#00D2FF] hover:bg-[#00D2FF] hover:text-[#050505] font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all shadow-sm font-bold"
                  data-testid="clarity-call-promo-cta-button"
                >
                  Book My Clarity Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 19 — FINAL CTA */}
      <section className="py-28 sm:py-36 relative overflow-hidden bg-[#111111]/20 border-t border-[#FF8C00]/15" data-testid="final-cta-section">
        <div className="absolute inset-0 glow-radial pointer-events-none opacity-25" />
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] block mb-4 font-bold">
            The Awakening Invitation
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light text-[#F6F4EE] leading-[1.15] max-w-4xl mx-auto mb-8">
            Your next chapter does not need to begin with a <span className="text-gold-shimmer italic">perfect plan.</span>
          </h2>
          <p className="font-sans text-base sm:text-lg text-[#AAA29E] max-w-3xl mx-auto mb-12 leading-relaxed">
            It can begin with simple awareness. Understand yourself. Reconnect with your deep values. Transform automated cycles, strengthen self-connection, and consciously design a life of deep alignment.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
            <a
              href="https://learn.eshawitheswari.com/l/96a7c7eae0"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-10 py-4.5 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full shadow-[0_8px_30px_rgba(255,140,0,0.3)] hover:scale-105 active:scale-95 transition-all"
              data-testid="final-primary-cta-button"
            >
              Attend the Masterclass
            </a>
            <Link
              to="/offerings"
              className="w-full sm:w-auto px-10 py-4.5 border border-[#FF8C00]/35 hover:border-[#FF8C00]/60 text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all bg-[#111111]/70 backdrop-blur-md"
              data-testid="final-secondary-cta-button"
            >
              Explore ESHA Offerings Hub
            </Link>
          </div>

          <div className="space-y-2 mt-12 border-t border-[#FF8C00]/15 pt-12">
            <p className="font-serif italic text-lg sm:text-2xl text-[#F6F4EE] font-light">
              “From where you are now to the person you are capable of becoming.”
            </p>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00D2FF] font-bold mt-1">
              — This is the ESHA journey.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
